import type { LandingPageContent } from '../types'

export const COMO_CALCULAMOS_EL_PRECIO: LandingPageContent = {
  slug: 'como-calculamos-el-precio',
  lang: 'es',
  topicId: 'methodik-preisberechnung',
  title: '¿Cómo calcula Roofora el precio? Metodología y datos',
  metaDescription:
    '¿De dónde sale el precio medio de la calculadora de costes? Datos, niveles de confianza y ' +
    'frecuencia de actualización explicados con transparencia - sin cifras inventadas, sin valoraciones falsas.',
  keywords: [
    'metodología calculadora fotovoltaica',
    'cómo calcula Roofora el precio',
    'datos estimación precio fotovoltaica',
    'Roofora precio medio cálculo',
    'orientación fotovoltaica rendimiento',
    'sombreado parcial pérdida de rendimiento',
    'orientación tejado fotovoltaica',
  ],
  eyebrow: 'Transparencia',
  h1: '¿Cómo calcula Roofora el precio?',
  intro:
    'El precio medio de la calculadora de costes no es una tabla fija ni una estimación propia de ' +
    'Roofora, sino que se calcula de nuevo en cada consulta a partir de presupuestos reales de ' +
    'empresas instaladoras enviados en la plataforma. Esta página explica qué datos se usan, cómo ' +
    'se gestiona una cantidad de datos todavía pequeña o creciente, y qué es lo que esta ' +
    'calculadora deliberadamente no hace.',
  sections: [
    { type: 'heading', text: 'Base de datos: presupuestos reales, sin cifras inventadas' },
    {
      type: 'paragraph',
      text:
        'Cada dato proviene de un presupuesto real de una empresa instaladora enviado en Roofora ' +
        'para una instalación completa (fotovoltaica, opcionalmente con batería). No existe un ' +
        'número mínimo de presupuestos a partir del cual se "activa" la función, ni valores ' +
        'redondeados o estimados como relleno - la base de datos crece con cada nuevo presupuesto, ' +
        'sea cual sea su tamaño en un momento dado.',
    },
    { type: 'heading', text: 'Cuatro niveles de confianza en lugar de una sola cifra' },
    {
      type: 'list',
      items: [
        'Promedio regional: si hay suficientes presupuestos comparables de su región (zona postal aproximada), se calcula únicamente con esos.',
        'Promedio nacional: si los presupuestos regionales no bastan pero sí hay suficientes comparables de toda Alemania, se muestra este promedio en su lugar - claramente marcado como "nacional".',
        'Estimación aproximada: si ni siquiera la comparación nacional basta para su tamaño de instalación/acumulador exacto, se calcula una estimación más general a partir del precio por kWp o por kWh de acumulador de todos los presupuestos de sistemas completos anteriores - claramente marcada como menos precisa.',
        'Datos insuficientes: si ni eso basta, se muestra honestamente "datos insuficientes" en lugar de inventar una cifra. Aun así puede solicitar una oferta gratuita y sin compromiso.',
      ],
    },
    {
      type: 'paragraph',
      text:
        'Este orden se recorre de nuevo en cada cálculo y nunca se mezcla en silencio - siempre ' +
        'sabrá en qué nivel de los cuatro se basa un resultado, no solo una cifra sin contexto.',
    },
    { type: 'heading', text: 'Actualización' },
    {
      type: 'paragraph',
      text:
        'No existe un ciclo de actualización separado - cada cálculo accede en tiempo real al ' +
        'estado actual de todos los presupuestos enviados hasta el momento. Cuantas más empresas ' +
        'instaladoras envíen presupuestos a través de Roofora, con más frecuencia se aplicará el ' +
        'nivel regional, más preciso, en lugar del nacional o la estimación aproximada.',
    },
    { type: 'heading', text: 'Lo que esta calculadora deliberadamente no hace' },
    {
      type: 'list',
      items: [
        'Sin valoraciones ni estrellas inventadas o redondeadas - mientras no existan valoraciones reales y transparentes de usuarios, tampoco se muestra ninguna.',
        'No es una oferta vinculante: la cifra es una estimación basada en presupuestos anteriores, no un precio fijo para su tejado concreto.',
        'No se comparte su código postal ni el resto de sus datos con terceros - la calculadora en sí no guarda nada.',
      ],
    },
    { type: 'heading', text: 'Orientación del tejado: cómo estima el rendimiento nuestra calculadora' },
    {
      type: 'paragraph',
      text:
        'Además del precio, nuestra calculadora de dimensionamiento gratuita también sigue el mismo ' +
        'principio de transparencia: nada de valores de relleno estimados, sino una base de cálculo ' +
        'abiertamente consultable. Para la orientación del tejado usa un factor de rendimiento fijo ' +
        'por orientación, con el sur como valor de referencia (100 %):',
    },
    {
      type: 'list',
      items: [
        'Sur: 100 % (valor de referencia)',
        'Sureste / Suroeste: 95 %',
        'Este / Oeste: 85 %',
        'Tejado plano: 90 %',
        'Noreste / Noroeste: 70 %',
        'Norte: 60 %',
      ],
    },
    {
      type: 'paragraph',
      text:
        'Estos valores se combinan con datos de irradiación según su ubicación (PVGIS) para calcular ' +
        'el tamaño de instalación recomendado. Si tiene varias superficies de tejado con distinta ' +
        'orientación (por ejemplo, un tejado este-oeste), simplemente indique cada superficie por ' +
        'separado con su propia orientación - la calculadora tiene en cuenta cada una de forma ' +
        'individual.',
    },
    { type: 'heading', text: 'Sombreado parcial: por qué nuestra estimación no lo incluye' },
    {
      type: 'paragraph',
      text:
        'El sombreado parcial (por ejemplo, por árboles, edificios vecinos o chimeneas) puede reducir ' +
        'el rendimiento anual de forma desproporcionada - según fuentes externas, ya un 10 % de ' +
        'sombra en una superficie de tejado puede reducir el rendimiento anual hasta un 15 %. Con ' +
        'honestidad: la calculadora de dimensionamiento gratuita no modela el sombreado, su ' +
        'estimación se basa únicamente en orientación, inclinación y datos de irradiación según la ' +
        'ubicación. La comprobación real del sombreado la realiza la empresa instaladora que ' +
        'responde a su solicitud, a partir de las fotos de su tejado o de una visita in situ - eso se ' +
        'refleja en su presupuesto individual, no en la estimación orientativa previa. Si tiene ' +
        'varias superficies de tejado con distinta orientación o parcialmente sombreadas, la empresa ' +
        'instaladora también planifica en su presupuesto la distribución eléctrica adecuada (por ' +
        'ejemplo, en varias entradas del inversor) - eso forma parte de la planificación profesional, ' +
        'no algo que usted deba definir de antemano.',
    },
  ],
  faq: [
    {
      q: '¿Por qué a veces veo "promedio nacional" en lugar de un precio regional?',
      a: 'Porque todavía no hay suficientes presupuestos comparables de su región. En cuanto más empresas instaladoras de su zona envíen presupuestos, el cálculo pasará automáticamente al promedio regional, más preciso.',
    },
    {
      q: '¿La cifra mostrada es una oferta vinculante?',
      a: 'No. Es una estimación basada en presupuestos anteriores de Roofora, no un precio vinculante para su tejado concreto. Solo recibirá una oferta vinculante de una empresa instaladora tras una solicitud real.',
    },
    {
      q: '¿Por qué no hay valoraciones con estrellas para la calculadora en sí?',
      a: 'Porque no inventamos valoraciones. En cuanto existan valoraciones reales y transparentes de usuarios, se mostrarán - no antes.',
    },
    {
      q: '¿Se tiene en cuenta el sombreado de mi tejado en la estimación automática?',
      a: 'No - la estimación automática solo tiene en cuenta orientación, inclinación y ubicación, no el sombreado individual. La comprobación real a partir de las fotos de su tejado la realiza la empresa instaladora que responde a su solicitud, como parte de su presupuesto.',
    },
  ],
  primaryCta: { label: 'Probar la calculadora de costes ahora', action: 'customer' },
  secondaryCta: {
    label: 'Coste de una instalación fotovoltaica: guía completa',
    action: 'link',
    href: '/coste-instalacion-fotovoltaica',
  },
  relatedLinks: [
    { label: 'Coste de una instalación fotovoltaica: guía completa', href: '/coste-instalacion-fotovoltaica' },
    { label: 'Cómo funciona la comparación de presupuestos', href: '/comparar-presupuestos-fotovoltaicos' },
    { label: 'Planificar la instalación paso a paso', href: '/planificar-instalacion-fotovoltaica' },
  ],
}
